import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import {
  useWatch,
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import {
  type ApplicationScore,
  type BaseCandidate,
  type BaseResume,
  type MatchReport,
  type ResumeEmployment,
  type ResumeParsingStatus,
  type Row,
  type Scorecard,
} from 'talent_matching/types';

const CACHE_KEY_PREFIX = 'my-greenhouse';

export type MyGreenhouseCandidate = BaseCandidate &
  Partial<ApplicationScore> & {
    matchedSkillsCount: number;
    profileLocation?: string | null;
    matchReport?: MatchReport | null;
    scorecards?: Scorecard[];
    workPreferences: {
      languages?: string[];
      employmentTypes?: string[];
      worksites?: string[];
      location?: string | null;
      willingToRelocate: boolean;
    } | null;
    resume?:
      | (BaseResume & {
          details: {
            address: string | null;
            employments: ResumeEmployment[];
          };
          parsing_status?: ResumeParsingStatus;
        })
      | null;
  };

type MyGreenhouseQueryData = {
  rows: Row[];
  totalRows: number;
};

const useQueryParams = () => {
  const { control } = useFormContext();
  const formValues = useWatch({ control });

  const activeCompanyFilters = (
    (formValues.companyNameFilters ?? []) as Array<{
      id: string;
      mode: string;
      value: string;
    }>
  ).filter((f) => f.value?.trim());

  const activeEducationFilters = (
    (formValues.educationFilters ?? []) as AdvancedFilter[]
  )
    .map((f) => new AdvancedFilter(f))
    .filter((f) => f.applied())
    .map(({ type, operator, value }) => ({ type, operator, value }));

  return {
    page: formValues.page as number,
    per_page: formValues.perPage as number,
    saved_for_later: formValues.savedForLater,
    can_have: formValues.canHave,
    must_have: formValues.mustHave,
    does_not_have: formValues.doesNotHave,
    years_of_experience_min: formValues.minExperience,
    years_of_experience_max: formValues.maxExperience,
    company_name_filters:
      activeCompanyFilters.length > 0 ? activeCompanyFilters : undefined,
    education_filters:
      activeEducationFilters.length > 0 ? activeEducationFilters : undefined,
    work_preference_languages: formValues.workPreferenceLanguages,
    work_preference_employment_types: formValues.workPreferenceEmploymentTypes,
    work_preference_worksites: formValues.workPreferenceWorksites,
    work_preference_locations: formValues.workPreferenceLocations,
    remote_candidates: formValues.remoteCandidates,
    work_preference_willing_to_relocate:
      formValues.workPreferenceWillingToRelocate,
  };
};

export const useMyGreenhouseCandidates = (
  path: string,
  { enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<MyGreenhouseQueryData> => {
  const queryClient = useQueryClient();
  const params = useQueryParams();
  const { page, ...filterParams } = params;

  const queryKey = [
    `${CACHE_KEY_PREFIX}-candidates`,
    filterParams,
    page,
  ] as const;

  const queryResult = useQuery<MyGreenhouseQueryData>({
    queryKey,
    queryFn: async () => {
      const { data } = await standardRequest.get(path, {
        params: { ...filterParams, page },
      });

      const rows: Row[] = data.candidates.map(
        (candidate: MyGreenhouseCandidate) => {
          queryClient.setQueryData(
            [`${CACHE_KEY_PREFIX}-candidate`, candidate.id],
            candidate
          );
          return { id: candidate.id };
        }
      );

      return {
        rows,
        totalRows: data.total,
      };
    },
    enabled,
    // Keep the previous page's data visible while the next page is fetching.
    // Without this, paginating briefly flips totalRows to 0, which triggers
    // the page-clamp effect in use_filter_table_state and snaps back to page 1.
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return queryResult;
};

export const useMyGreenhouseCandidate = (
  candidateId: number
): { candidate: MyGreenhouseCandidate | undefined } => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [`${CACHE_KEY_PREFIX}-candidate`, candidateId],
    staleTime: Infinity,
    queryFn: () =>
      queryClient.getQueryData<MyGreenhouseCandidate>([
        `${CACHE_KEY_PREFIX}-candidate`,
        candidateId,
      ]),
    retry: 1,
  });

  return {
    candidate: query.data,
  };
};
